import { computed, onActivated, onMounted, reactive, ref } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';

function cloneValueInfo(valueInfo) {
  return {
    value: valueInfo?.value || '',
    length: Number(valueInfo?.length || 0),
    preview: valueInfo?.preview || '',
    empty: !!valueInfo?.empty,
  };
}

export function useMazdaAccountSettings(mazdaPost) {
  const loading = ref(false);
  const saving = ref(false);
  const refreshingCookie = ref(false);
  const redisDb = ref('DB1 / mazda');

  const keys = reactive({
    account: { key: 'Account:1', type: 'hash' },
    tokenCookie: { key: 'api:funcMazda:gxmAuthorisationToken', type: 'string' },
    idTokenCookie: { key: 'api:funcMazda:gxmAuthorisationIdToken', type: 'string' },
    ggpVersion: { key: 'api:GgpVersion:OL_VER', type: 'string' },
  });

  const accountForm = reactive({
    number: '',
    cipher: '',
  });

  const cookieForm = reactive({
    gxmAuthorisationToken: '',
    gxmAuthorisationIdToken: '',
  });

  const cookieInfo = reactive({
    gxmAuthorisationToken: cloneValueInfo(),
    gxmAuthorisationIdToken: cloneValueInfo(),
  });

  const ggpForm = reactive({
    version: '',
  });

  const tokenLengthText = computed(() => `${cookieInfo.gxmAuthorisationToken.length || 0} 字符`);
  const idTokenLengthText = computed(() => `${cookieInfo.gxmAuthorisationIdToken.length || 0} 字符`);

  const applyInfo = (data) => {
    redisDb.value = data.redisDb || 'DB1 / mazda';

    if (data.keys?.account) Object.assign(keys.account, data.keys.account);
    if (data.keys?.tokenCookie) Object.assign(keys.tokenCookie, data.keys.tokenCookie);
    if (data.keys?.idTokenCookie) Object.assign(keys.idTokenCookie, data.keys.idTokenCookie);
    if (data.keys?.ggpVersion) Object.assign(keys.ggpVersion, data.keys.ggpVersion);

    accountForm.number = data.account?.number || '';
    accountForm.cipher = data.account?.cipher || '';

    const token = cloneValueInfo(data.cookies?.gxmAuthorisationToken);
    const idToken = cloneValueInfo(data.cookies?.gxmAuthorisationIdToken);
    Object.assign(cookieInfo.gxmAuthorisationToken, token);
    Object.assign(cookieInfo.gxmAuthorisationIdToken, idToken);
    cookieForm.gxmAuthorisationToken = token.value;
    cookieForm.gxmAuthorisationIdToken = idToken.value;

    ggpForm.version = data.ggpVersion || '';
  };

  const loadInfo = async () => {
    if (loading.value) return;

    loading.value = true;
    try {
      const response = await mazdaPost('accountSettingsInfo');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '读取账号/GGP设置失败');
        return;
      }
      applyInfo(data);
    } catch (error) {
      ElMessage.error(`读取账号/GGP设置失败: ${error.response?.data?.message || error.message}`);
    } finally {
      loading.value = false;
    }
  };

  const saveAccount = async () => {
    const number = String(accountForm.number || '').trim();
    const cipher = String(accountForm.cipher || '').trim();
    if (!number) {
      ElMessage.error('账号不能为空');
      return;
    }
    if (!cipher) {
      ElMessage.error('密码不能为空');
      return;
    }

    try {
      await ElMessageBox.confirm(
        `确定修改马自达账号密码吗？\n\n账号：${number}\n密码：${cipher}`,
        '确认修改账号密码',
        { confirmButtonText: '确定修改', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await mazdaPost('accountSettingsSaveAccount', { number, cipher });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存账号失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || '账号密码已保存');
    } catch (error) {
      ElMessage.error(`保存账号失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const saveCookie = async () => {
    const token = String(cookieForm.gxmAuthorisationToken || '').trim();
    const idToken = String(cookieForm.gxmAuthorisationIdToken || '').trim();
    if (!token) {
      ElMessage.error('gxmAuthorisationToken 不能为空');
      return;
    }
    if (!idToken) {
      ElMessage.error('gxmAuthorisationIdToken 不能为空');
      return;
    }

    try {
      await ElMessageBox.confirm(
        '确定覆盖当前两个 Mazda 在线 cookie 吗？',
        '确认修改 Cookie',
        { confirmButtonText: '确定覆盖', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await mazdaPost('accountSettingsSaveCookie', {
        gxmAuthorisationToken: token,
        gxmAuthorisationIdToken: idToken,
      });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存 Cookie 失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || 'Cookie 已保存');
    } catch (error) {
      ElMessage.error(`保存 Cookie 失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const refreshCookie = async () => {
    if (refreshingCookie.value) return;

    refreshingCookie.value = true;
    try {
      const response = await mazdaPost('accountSettingsRefreshCookie', {}, { timeout: 120000 });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '获取 Cookie 失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || 'Cookie 已更新');
    } catch (error) {
      ElMessage.error(`获取 Cookie 失败: ${error.response?.data?.message || error.message}`);
    } finally {
      refreshingCookie.value = false;
    }
  };

  const clearCookie = async () => {
    try {
      await ElMessageBox.confirm(
        '确定清空当前两个 Mazda 在线 cookie 吗？清空后下次在线请求会重新获取。',
        '确认清空 Cookie',
        { confirmButtonText: '确定清空', cancelButtonText: '取消', type: 'warning' },
      );
    } catch {
      return;
    }

    saving.value = true;
    try {
      const response = await mazdaPost('accountSettingsClearCookie');
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '清空 Cookie 失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || 'Cookie 已清空');
    } catch (error) {
      ElMessage.error(`清空 Cookie 失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  const saveGgpVersion = async () => {
    const version = String(ggpForm.version || '').trim();
    if (!/^0[xX][0-9A-Fa-f]{8}$/.test(version)) {
      ElMessage.error('GGP 版本格式应为 0x 加 8 位十六进制');
      return;
    }

    saving.value = true;
    try {
      const response = await mazdaPost('accountSettingsSaveGgpVersion', { version });
      const data = response.data || {};
      if (!data.success) {
        ElMessage.error(data.message || '保存 GGP 版本失败');
        return;
      }
      applyInfo(data);
      ElMessage.success(data.message || 'GGP 版本已保存');
    } catch (error) {
      ElMessage.error(`保存 GGP 版本失败: ${error.response?.data?.message || error.message}`);
    } finally {
      saving.value = false;
    }
  };

  onMounted(loadInfo);
  onActivated(loadInfo);

  return {
    loading,
    saving,
    refreshingCookie,
    redisDb,
    keys,
    accountForm,
    cookieForm,
    cookieInfo,
    ggpForm,
    tokenLengthText,
    idTokenLengthText,
    loadInfo,
    saveAccount,
    saveCookie,
    refreshCookie,
    saveGgpVersion,
  };
}
